/**
 * Model Catalog page component.
 * Displays a read-only catalog of AI providers and their models.
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Collapse,
  IconButton,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Cloud as CloudIcon,
  Token as TokenIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
} from '@mui/icons-material';
import { llmService } from '../services/llmService';
import { useNotificationStore } from '../store/notificationStore';
import { TextField } from '../@core/components/mui';
import type { LlmProviderDto } from '../types/llm';

/**
 * Format token count for display (e.g., 128000 -> "128K").
 */
const formatTokens = (tokens: number): string => {
  if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(1)}M`;
  if (tokens >= 1000) return `${(tokens / 1000).toFixed(0)}K`;
  return tokens.toString();
};

/**
 * Format cost for display.
 */
const formatCost = (cost?: number): string => {
  if (cost === undefined || cost === null) return '-';
  if (cost === 0) return 'Free';
  if (cost < 0.001) return `$${(cost * 1000000).toFixed(2)}/1M`;
  return `$${cost.toFixed(4)}`;
};

export const ModelCatalogPage = () => {
  const [providers, setProviders] = useState<LlmProviderDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedProviders, setExpandedProviders] = useState<Set<string>>(new Set());
  const { addNotification } = useNotificationStore();

  useEffect(() => {
    loadProviders();
  }, []);

  const loadProviders = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await llmService.getProviders(true);
      setProviders(data);
      // Expand all providers by default
      setExpandedProviders(new Set(data.map((p) => p.id)));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load model catalog');
      addNotification(err.response?.data?.message || 'Failed to load model catalog', 'error');
    } finally {
      setLoading(false);
    }
  };

  const toggleProvider = (providerId: string) => {
    setExpandedProviders((prev) => {
      const next = new Set(prev);
      if (next.has(providerId)) {
        next.delete(providerId);
      } else {
        next.add(providerId);
      }
      return next;
    });
  };

  // Filter providers and models by search term
  const filteredProviders = providers
    .map((provider) => ({
      ...provider,
      models: provider.models.filter(
        (model) =>
          model.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          model.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          provider.displayName.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    }))
    .filter(
      (provider) =>
        provider.models.length > 0 ||
        provider.displayName.toLowerCase().includes(searchTerm.toLowerCase())
    );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Model Catalog
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Browse available AI providers and models. Configure provider credentials in Tenant Settings to use these models.
          </Typography>
        </Box>
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search */}
      <Box sx={{ mb: 4 }}>
        <TextField
          placeholder="Search providers or models..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fullWidth
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <CloudIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="h3" color="primary" sx={{ fontWeight: 700 }}>
                {providers.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Providers
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="h3" color="success.main" sx={{ fontWeight: 700 }}>
                {providers.reduce((sum, p) => sum + p.modelCount, 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Models
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="h3" color="info.main" sx={{ fontWeight: 700 }}>
                {providers.filter((p) => p.isActive).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Providers
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Provider List */}
      {filteredProviders.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography color="text.secondary">
              {searchTerm ? 'No providers or models match your search.' : 'No providers available.'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        filteredProviders.map((provider) => (
          <Card key={provider.id} sx={{ mb: 3 }}>
            {/* Provider Header */}
            <CardContent
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
                py: 2,
              }}
              onClick={() => toggleProvider(provider.id)}
            >
              <CloudIcon sx={{ mr: 2, color: 'primary.main' }} />
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {provider.displayName}
                  </Typography>
                  <Chip
                    label={provider.isSystem ? 'System' : 'Custom'}
                    size="small"
                    color={provider.isSystem ? 'default' : 'primary'}
                    variant="outlined"
                  />
                  <Chip
                    label={provider.isActive ? 'Active' : 'Inactive'}
                    size="small"
                    color={provider.isActive ? 'success' : 'default'}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {provider.modelCount} active model{provider.modelCount !== 1 ? 's' : ''}
                </Typography>
              </Box>
              <IconButton size="small">
                {expandedProviders.has(provider.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </CardContent>

            {/* Models Table */}
            <Collapse in={expandedProviders.has(provider.id)}>
              <TableContainer component={Paper} variant="outlined" sx={{ mx: 2, mb: 2, borderRadius: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Model</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        <Tooltip title="Maximum context window size">
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                            <TokenIcon fontSize="small" />
                            Context
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        <Tooltip title="Maximum output tokens">
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                            <TokenIcon fontSize="small" />
                            Output
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        <Tooltip title="Cost per input token">
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                            <MoneyIcon fontSize="small" />
                            Input Cost
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        <Tooltip title="Cost per output token">
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                            <MoneyIcon fontSize="small" />
                            Output Cost
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {provider.models.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            No active models for this provider.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      provider.models.map((model) => (
                        <TableRow key={model.id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {model.displayName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                              {model.name}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={formatTokens(model.maxContextTokens)}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={formatTokens(model.maxOutputTokens)}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="text.secondary">
                              {formatCost(model.costPerInputToken)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="text.secondary">
                              {formatCost(model.costPerOutputToken)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {model.isActive ? (
                              <ActiveIcon fontSize="small" color="success" />
                            ) : (
                              <InactiveIcon fontSize="small" color="disabled" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </Card>
        ))
      )}
    </Box>
  );
};
